/**
 * A dropped card should appear in its new column at once. The board's data is a page per
 * status, so until the refetch lands the card is still in the group it came from — applying
 * the move here keeps the drop from looking like it did nothing and then jumping.
 *
 * Counts move with the card: the source column loses one and the destination gains one, so
 * the "Showing 1-20 of 296" line stays honest while the request is in flight.
 */
export const applyPendingMoves = (groups, pendingMoves) => {
  if (!pendingMoves || Object.keys(pendingMoves).length === 0) return groups ?? {};

  const source = groups ?? {};
  const moved = [];

  // Take the moved cards out of whichever group currently holds them.
  const trimmed = Object.entries(source).reduce((acc, [status, group]) => {
    const content = group?.content ?? [];
    const leaving = content.filter((task) => {
      const target = pendingMoves[task?.id];
      return target != null && target !== status;
    });
    leaving.forEach((task) => moved.push({ task, target: pendingMoves[task.id] }));
    if (leaving.length === 0) return { ...acc, [status]: group };
    const leavingIds = new Set(leaving.map((task) => task.id));
    return {
      ...acc,
      [status]: {
        ...group,
        content: content.filter((task) => !leavingIds.has(task.id)),
        totalItems: Math.max(0, (Number(group?.totalItems) || 0) - leaving.length),
      },
    };
  }, {});

  if (moved.length === 0) return trimmed;

  // Put them at the top of the destination, where a just-moved card is easiest to spot.
  return moved.reduce((acc, { task, target }) => {
    const group = acc[target];
    if (!group) return acc;
    const content = group.content ?? [];
    if (content.some((existing) => existing?.id === task.id)) return acc;
    return {
      ...acc,
      [target]: {
        ...group,
        content: [{ ...task, status: target }, ...content],
        totalItems: (Number(group.totalItems) || 0) + 1,
      },
    };
  }, trimmed);
};
